import { LockOutlined, EmailOutlined, ErrorOutline } from '@mui/icons-material';
import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { useAdminSignup, useValidateToken } from '@/hooks/queries/useAdminOnboarding';

export const AdminOnboardingPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');

  const {
    data: tokenData,
    isLoading: isValidating,
    isError: isTokenError,
  } = useValidateToken(token);
  const signupMutation = useAdminSignup();

  useEffect(() => {
    if (!token) {
      void navigate('/login', { replace: true });
    }
  }, [token, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordError('');

    if (password.length < 8) {
      setPasswordError('La contraseña debe tener al menos 8 caracteres.');
      return;
    }
    if (password !== confirmPassword) {
      setPasswordError('Las contraseñas no coinciden.');
      return;
    }

    try {
      const result = await signupMutation.mutateAsync({ token: token!, password });
      // Store auth tokens
      localStorage.setItem('finara_token', result.access_token);
      localStorage.setItem('finara_token_expires', String(Date.now() + result.expires_in * 1000));
      localStorage.setItem('finara_user_id', result.user.id);
      void navigate('/admin-onboarding/funnel', { replace: true });
    } catch (_) {
      // Error handled via mutation state
    }
  };

  const inputClass =
    'w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent';

  if (isValidating) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <p className="text-gray-500 text-sm">Validando invitación...</p>
      </div>
    );
  }

  if (isTokenError || (tokenData && !tokenData.valid)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 max-w-md w-full text-center">
          <ErrorOutline className="text-red-400 mb-4" style={{ fontSize: 48 }} />
          <h2 className="text-lg font-semibold text-gray-800 mb-2">Invitación inválida</h2>
          <p className="text-sm text-gray-500">
            {tokenData?.expired
              ? 'Este enlace de invitación ha expirado. Contacta a tu administrador para recibir una nueva invitación.'
              : 'El enlace de invitación no es válido o ya fue utilizado.'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 max-w-md w-full">
        <div className="text-center mb-8">
          <div className="w-14 h-14 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <LockOutlined className="text-blue-600" />
          </div>
          <h1 className="text-xl font-bold text-gray-900">Crea tu cuenta</h1>
          <p className="text-sm text-gray-500 mt-2">
            Has sido invitado a registrarte como administrador en Finara.
          </p>
          {tokenData?.email && (
            <div className="flex items-center justify-center gap-2 mt-3 text-sm text-gray-700 bg-gray-50 rounded-lg px-3 py-2">
              <EmailOutlined fontSize="small" className="text-gray-400" />
              <span>{tokenData.email}</span>
            </div>
          )}
        </div>

        <form onSubmit={e => void handleSubmit(e)} className="space-y-4">
          <div>
            <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
              Contraseña *
            </label>
            <input
              className={inputClass}
              type="password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              placeholder="Mínimo 8 caracteres"
              required
              minLength={8}
            />
          </div>

          <div>
            <label
              htmlFor="confirmPassword"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Confirmar contraseña *
            </label>
            <input
              className={inputClass}
              type="password"
              value={confirmPassword}
              onChange={e => setConfirmPassword(e.target.value)}
              placeholder="Repite tu contraseña"
              required
            />
          </div>

          {passwordError && <p className="text-sm text-red-600">{passwordError}</p>}

          {signupMutation.isError && (
            <p className="text-sm text-red-600">
              {signupMutation.error?.message ?? 'Error al crear la cuenta. Intenta nuevamente.'}
            </p>
          )}

          <button
            type="submit"
            disabled={signupMutation.isPending}
            className="w-full py-2.5 px-4 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-60"
          >
            {signupMutation.isPending ? 'Creando cuenta...' : 'Crear cuenta y continuar'}
          </button>
        </form>
      </div>
    </div>
  );
};
